import React, {FC, useEffect, useMemo, useRef, useState} from "react"
import {
    Badge,
    Button,
    ListGroup,
} from "react-bootstrap"
import {useTranslation} from "react-i18next"

import {
    getTenantLabel,
    TenantTreeNode,
} from "./tenant.types"

type TenantTreeProps = {
    nodes: TenantTreeNode[]
    selectedTenantId?: string | null
    highlightedTenantIds?: string[]
    highlightedLabel?: string
    revealTenantIds?: string[]
    focusTenantId?: string | null
    focusRequestKey?: number
    onSelect?: (tenant: TenantTreeNode) => void
    actionLabel?: string
    onAction?: (tenant: TenantTreeNode) => void
    actionDisabled?: (tenant: TenantTreeNode) => boolean
}

type TenantNodeProps = {
    node: TenantTreeNode
    level: number
    selectedTenantId?: string | null
    highlightedTenantIds: Set<string>
    highlightedLabel: string
    revealTenantIds: Set<string>
    focusTenantId?: string | null
    focusRequestKey: number
    onSelect?: (tenant: TenantTreeNode) => void
    actionLabel?: string
    onAction?: (tenant: TenantTreeNode) => void
    actionDisabled?: (tenant: TenantTreeNode) => boolean
}

const TenantNode: FC<TenantNodeProps> = ({
    node,
    level,
    selectedTenantId,
    highlightedTenantIds,
    highlightedLabel,
    revealTenantIds,
    focusTenantId,
    focusRequestKey,
    onSelect,
    actionLabel,
    onAction,
    actionDisabled,
}) => {
    const {t} = useTranslation("examManagement")
    const [expanded, setExpanded] = useState<boolean>(level < 1)
    const itemRef = useRef<HTMLAnchorElement | null>(null)
    const hasChildren = node.children.length > 0
    const selected = selectedTenantId === node._id
    const highlighted = highlightedTenantIds.has(node._id)

    const containsRevealedTenant = useMemo(() => {
        const contains = (candidate: TenantTreeNode): boolean =>
            revealTenantIds.has(candidate._id) ||
            candidate.children.some(contains)

        return node.children.some(contains)
    }, [node.children, revealTenantIds])

    useEffect(() => {
        if (containsRevealedTenant) {
            setExpanded(true)
        }
    }, [containsRevealedTenant])

    useEffect(() => {
        if (focusTenantId === node._id && focusRequestKey > 0) {
            itemRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            })
        }
    }, [focusTenantId, focusRequestKey, node._id])

    return (
        <>
            <ListGroup.Item
                ref={itemRef}
                active={selected}
                className={[
                    "d-flex align-items-center gap-2",
                    highlighted && !selected
                        ? "border border-warning"
                        : "",
                ].join(" ")}
                style={{
                    paddingLeft: `${0.75 + level * 1.25}rem`,
                }}
            >
                <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    disabled={!hasChildren}
                    onClick={() => setExpanded(previous => !previous)}
                >
                    <i
                        className={
                            hasChildren
                                ? expanded
                                    ? "bi bi-chevron-down"
                                    : "bi bi-chevron-right"
                                : "bi bi-dot"
                        }
                    />
                </Button>

                <button
                    type="button"
                    className="btn btn-link text-start text-decoration-none flex-grow-1 p-0"
                    onClick={() => onSelect?.(node)}
                >
                    <i
                        className={
                            node.isDepartment
                                ? "bi bi-diagram-3 me-2"
                                : node.isDean
                                    ? "bi bi-bank me-2"
                                    : node.isInfrastructure
                                        ? "bi bi-hdd-network me-2"
                                        : "bi bi-building me-2"
                        }
                    />
                    {getTenantLabel(node)}
                </button>

                <div className="d-flex flex-wrap gap-1">
                    {node.isDepartment && (
                        <Badge bg="primary">
                            {t("treeFlags.department")}
                        </Badge>
                    )}

                    {node.isDean && (
                        <Badge bg="secondary">
                            {t("treeFlags.dean")}
                        </Badge>
                    )}

                    {node.isInfrastructure && (
                        <Badge bg="dark">
                            {t("treeFlags.infrastructure")}
                        </Badge>
                    )}

                    {highlighted && (
                        <Badge bg="warning" text="dark">
                            {highlightedLabel}
                        </Badge>
                    )}
                </div>

                {actionLabel && onAction && (
                    <Button
                        size="sm"
                        disabled={
                            actionDisabled
                                ? actionDisabled(node)
                                : false
                        }
                        onClick={() => onAction(node)}
                    >
                        {actionLabel}
                    </Button>
                )}
            </ListGroup.Item>

            {expanded && node.children.map(child => (
                <TenantNode
                    key={child._id}
                    node={child}
                    level={level + 1}
                    selectedTenantId={selectedTenantId}
                    highlightedTenantIds={highlightedTenantIds}
                    highlightedLabel={highlightedLabel}
                    revealTenantIds={revealTenantIds}
                    focusTenantId={focusTenantId}
                    focusRequestKey={focusRequestKey}
                    onSelect={onSelect}
                    actionLabel={actionLabel}
                    onAction={onAction}
                    actionDisabled={actionDisabled}
                />
            ))}
        </>
    )
}

const TenantTree: FC<TenantTreeProps> = ({
    nodes,
    selectedTenantId,
    highlightedTenantIds = [],
    highlightedLabel,
    revealTenantIds = [],
    focusTenantId,
    focusRequestKey = 0,
    onSelect,
    actionLabel,
    onAction,
    actionDisabled,
}) => {
    const {t} = useTranslation("examManagement")
    const resolvedHighlightedLabel = highlightedLabel ?? t("treeFlags.personAccess")
    const highlightedSet = useMemo(
        () => new Set(highlightedTenantIds),
        [highlightedTenantIds]
    )
    const revealSet = useMemo(
        () => new Set(revealTenantIds),
        [revealTenantIds]
    )

    return (
        <ListGroup>
            {nodes.map(node => (
                <TenantNode
                    key={node._id}
                    node={node}
                    level={0}
                    selectedTenantId={selectedTenantId}
                    highlightedTenantIds={highlightedSet}
                    highlightedLabel={resolvedHighlightedLabel}
                    revealTenantIds={revealSet}
                    focusTenantId={focusTenantId}
                    focusRequestKey={focusRequestKey}
                    onSelect={onSelect}
                    actionLabel={actionLabel}
                    onAction={onAction}
                    actionDisabled={actionDisabled}
                />
            ))}
        </ListGroup>
    )
}

export default TenantTree
